import { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import DispatcherLogin from './DispatcherLogin';
import DispatcherDashboard from './DispatcherDashboard';
import DispatcherSchedules from './DispatcherSchedules';
import { API_BASE_URL as SHARED_API_BASE_URL } from '../../apiConfig';

// API配置
const getApiBaseUrl = () => {
  return SHARED_API_BASE_URL;
};

export const API_BASE_URL = getApiBaseUrl();

// Token管理
export const getDispatcherToken = () => localStorage.getItem('dispatcher_token');
export const setDispatcherToken = (token) => localStorage.setItem('dispatcher_token', token);
export const removeDispatcherToken = () => localStorage.removeItem('dispatcher_token');
export const getDispatcherInfo = () => {
  try {
    return JSON.parse(localStorage.getItem('dispatcher_info') || 'null');
  } catch {
    return null;
  }
};
export const setDispatcherInfo = (info) => localStorage.setItem('dispatcher_info', JSON.stringify(info));
export const removeDispatcherInfo = () => localStorage.removeItem('dispatcher_info');

export default function DispatcherApp() {
  const [dispatcher, setDispatcher] = useState(getDispatcherInfo());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const generation = useRef(0);
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    generation.current += 1;
    localStorage.setItem('dispatcher_logged_out', '1');
    const token = getDispatcherToken();
    if (token) {
      fetch(`${API_BASE_URL}/dispatcher/auth/logout`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, keepalive: true
      }).catch(() => console.warn('服务器退出确认失败，本机已退出'));
    }
    removeDispatcherToken();
    removeDispatcherInfo();
    setDispatcher(null);
    setIsAuthenticated(false);
    setChecking(false);
    navigate('/dispatcher/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    const current = ++generation.current;
    const controller = new AbortController();
    const active = () => generation.current === current && !controller.signal.aborted;
    const verify = async () => {
      try {
        if (localStorage.getItem('dispatcher_logged_out')) return;
        const token = getDispatcherToken();
        const adminToken = localStorage.getItem('token');
        if (!token && !adminToken) return;
        const response = await fetch(`${API_BASE_URL}/dispatcher/${token ? 'me' : 'auth/admin-sso'}`, {
          method: token ? 'GET' : 'POST',
          headers: { Authorization: `Bearer ${token || adminToken}` },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('登录已失效，请重新登录');
        const data = await response.json();
        if (!active() || localStorage.getItem('dispatcher_logged_out')) return;
        const profile = token ? data : data.dispatcher;
        if (!token) setDispatcherToken(data.access_token);
        setDispatcherInfo(profile);
        setDispatcher(profile);
        setIsAuthenticated(true);
      } catch (error) {
        if (!active()) return;
        removeDispatcherToken();
        removeDispatcherInfo();
        setDispatcher(null);
        setIsAuthenticated(false);
      } finally {
        if (active()) setChecking(false);
      }
    };
    verify();
    const syncLogout = (event) => {
      if ((event.key === 'dispatcher_logged_out' && event.newValue) ||
          (event.key === 'dispatcher_token' && !event.newValue) || event.key === null) {
        generation.current += 1;
        controller.abort();
        setDispatcher(null);
        setIsAuthenticated(false);
        setChecking(false);
        navigate('/dispatcher/login', { replace: true });
      }
    };
    window.addEventListener('storage', syncLogout);
    return () => {
      controller.abort();
      window.removeEventListener('storage', syncLogout);
    };
  }, [navigate]);

  const handleLogin = (token, dispatcherData) => {
    generation.current += 1;
    localStorage.removeItem('dispatcher_logged_out');
    setChecking(false);
    setDispatcherToken(token);
    setDispatcherInfo(dispatcherData);
    setDispatcher(dispatcherData);
    setIsAuthenticated(true);
    navigate('/dispatcher');
  };

  if (checking) return <div role="status" className="p-8">正在验证登录…</div>;

  return (
    <Routes>
      <Route 
        path="login" 
        element={
          isAuthenticated 
            ? <Navigate to="/dispatcher" replace /> 
            : <DispatcherLogin onLogin={handleLogin} />
        } 
      />
      <Route 
        path="/" 
        element={
          isAuthenticated 
            ? <DispatcherDashboard dispatcher={dispatcher} onLogout={handleLogout} />
            : <Navigate to="/dispatcher/login" replace />
        } 
      />
      <Route 
        path="schedules" 
        element={
          isAuthenticated 
            ? <DispatcherSchedules dispatcher={dispatcher} onLogout={handleLogout} />
            : <Navigate to="/dispatcher/login" replace />
        } 
      />
      <Route path="*" element={<Navigate to="/dispatcher" replace />} />
    </Routes>
  );
}
